import { IPaginatedResponse } from '../../types/paginated-response.type'
import { IError } from '../base/error'
import ServiceBase from '../base/service.base'
import { IQueryPagination } from '../types/query.pagination.type'

import {
  IAppendInvoiceOriginRequestDTO,
  ICountDTO,
  IInvoiceCompleteResponseDTO,
  IInvoiceInternalDataDTO,
  InvoiceCompleteResponseDTO,
  InvoicePendingResponseDTO,
  InvoiceResponseDTO,
  IPaginatedCompanyInvoiceResponse,
  IPaginatedInvoiceItemsResponse,
  IRawFindAllPendingInvoicesWithCompanyDTO,
} from './invoice.types'
//import FormData from "form-data";

export class Invoices extends ServiceBase {
  /**
   * Set origins for already insert invoice
   *
   * @param data data to be stored (invoiceCode: old invoice, origins: origins from this invoice)
   * @returns boolean/true representing the request success
   */
  public async append(data: IAppendInvoiceOriginRequestDTO): Promise<boolean> {
    const url = '/invoice'

    const response = await this.request.post<boolean, IError>(url, data)

    if (response.status === 201) {
      return true
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Attach a file to an invoice
   * Files extensions allowed: 'png', 'pdf', 'jpg','jpeg'
   *
   * @param providerTaxId
   * @param invoiceCode invoice to attach the file
   * @param file file to be attached
   * @param jwt user jwt
   * @returns
   */
  public async uploadFile(file: File): Promise<string> {
    const url = `/invoice/file`

    const data = new FormData()
    data.append('file', file)

    const response = await this.request.post<string, IError>(url, data)

    if (response.status === 201) {
      return response.result ?? ''
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get pending invoices from user company
   *
   * @param jwt user jwt
   * @param pagination pagination is optional
   * @returns data representing pending invoices
   */
  public async getPending(
    pagination?: IQueryPagination,
  ): Promise<InvoicePendingResponseDTO[]> {
    const url = pagination
      ? `/invoice/pending/?page=${pagination.page}&limit=${pagination.limit}&filterByBeneficiaryCompany=true`
      : `/invoice/pending?filterByBeneficiaryCompany=true`

    const response = await this.request.get<
      InvoicePendingResponseDTO[],
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getPendingInvoice(
    invoiceCode: string,
  ): Promise<InvoicePendingResponseDTO> {
    const url = `/invoice/pending/search/${invoiceCode}?filterByBeneficiaryCompany=true`

    const response = await this.request.get<InvoicePendingResponseDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getPendingCount(): Promise<ICountDTO> {
    const url = `/invoice/pending/count?filterByBeneficiaryCompany=true`

    const response = await this.request.get<ICountDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get non pending invoices (invoices with origin) from user company
   *
   * @param jwt user jwt
   * @param pagination pagination is optional
   * @returns data representing completed invoices
   */
  public async getComplete(
    pagination?: IQueryPagination,
  ): Promise<InvoiceCompleteResponseDTO[]> {
    const url = pagination
      ? `/invoice/complete/?page=${pagination.page}&limit=${pagination.limit}&filterByBeneficiaryCompany=true`
      : `/invoice/complete?filterByBeneficiaryCompany=true`

    const response = await this.request.get<
      InvoiceCompleteResponseDTO[],
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getCompleteInvoice(
    invoiceCode: string,
  ): Promise<IInvoiceCompleteResponseDTO> {
    const url = `/invoice/complete/search/${invoiceCode}?filterByBeneficiaryCompany=true`

    const response = await this.request.get<
      IInvoiceCompleteResponseDTO,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getCompleteCount(): Promise<ICountDTO> {
    const url = `/invoice/complete/count?filterByBeneficiaryCompany=true`

    const response = await this.request.get<ICountDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * @deprecated
   */
  public async getAllPendingInvoices(): Promise<
    IRawFindAllPendingInvoicesWithCompanyDTO[]
  > {
    const url = `/invoice/pending/full`

    const response = await this.request.get<
      IRawFindAllPendingInvoicesWithCompanyDTO[],
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get the invoice data
   *
   */
  public async getInvoice(
    invoiceCode: string,
    providerTaxId: string,
  ): Promise<IInvoiceInternalDataDTO> {
    const url = `/invoice/${invoiceCode}/${providerTaxId}`

    const response = await this.request.get<IInvoiceInternalDataDTO, IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  /**
   * Get invoice items traceability
   *
   * @param invoiceId invoice id
   * @param page page number
   * @param jwt user jwt
   * @returns item traceability
   */
  public async invoiceItems(
    invoiceId: string,
    page: number,
    limit?: number,
  ): Promise<IPaginatedInvoiceItemsResponse> {
    const url = `/invoice/${invoiceId}/items`

    const response = await this.request.get<
      IPaginatedInvoiceItemsResponse,
      IError
    >(url, {
      params: {
        limit: limit || 10,
        page,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }
  /**
   * Get all invoices issued by a company
   *
   */
  public async getAllInvoices(
    providerTaxId: string,
    page: number,
    limit?: number,
    code?: string,
  ): Promise<IPaginatedCompanyInvoiceResponse> {
    const url = `/invoice/${providerTaxId}`

    const response = await this.request.get<
      IPaginatedCompanyInvoiceResponse,
      IError
    >(url, {
      params: {
        limit: limit || 10,
        page,
        code: code || undefined,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getCompletedInvoices(
    page?: number,
    limit?: number,
    search?: string,
  ): Promise<IPaginatedResponse<IInvoiceCompleteResponseDTO[]>> {
    const url = `/invoice`

    const response = await this.request.get<
      IPaginatedResponse<IInvoiceCompleteResponseDTO[]>,
      IError
    >(url, {
      params: {
        limit: limit || 10,
        page,
        search: search || undefined,
        pending: false,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getInvoiceOrigins(
    id: string,
    page: number,
    limit = 10,
  ): Promise<IPaginatedResponse<InvoiceResponseDTO[]>> {
    const url = `/invoice/${id}/origins`

    const response = await this.request.get<
      IPaginatedResponse<InvoiceResponseDTO[]>,
      IError
    >(url, {
      params: {
        limit: limit,
        page,
      },
    })

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getInvoiceById(id: string): Promise<InvoiceResponseDTO> {
    const url = `/invoice/id/${id}`
    const response = await this.request.get<InvoiceResponseDTO, IError>(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }
}
